import { usePassThrough } from 'primevue/passthrough';
import { selectField } from '../shared/select-field';

export const autocomplete = usePassThrough(selectField, {
  root: ({ props }: Record<string, any>) => ({
    class: [
      'relative min-w-0',
      // Flex
      'inline-flex',
      // Size
      { 'w-80 max-w-full': !props.multiple && props.size !== 'large' },
      { 'w-full': props.multiple },
      // Color
      { 'text-grey-800': !props.disabled },
      { 'text-grey-400': props.disabled },
      // States
      { 'select-none pointer-events-none cursor-default': props.disabled },
    ],
  }),
  pcInputText: ({ props, state }: Record<string, any>) => ({
    root: {
      class: [
        'min-w-0 group-[&>*:not(:first-child)]/input-group:rounded-l-none group-[&>*:not(:last-child)]/input-group:rounded-r-none',
        // Font
        'font-sans leading-none',
        // Position
        'z-0',
        'grow min-w-0',
        // Shape
        'appearance-none outline-none rounded-lg',
        { 'shadow-none rounded-none': props.multiple },
        { 'w-full': props.multiple },
        // Spacing
        'm-0',
        {
          'em:py-2 em:px-3': !props.multiple,
          '!pr-[2.875rem]': props.dropdown && !props.multiple,
          'p-0': props.multiple,
          'pr-[1.875rem]': props.dropdown && props.multiple,
        },
        // Colors
        'focus:filter-none hover:filter-none text-inherit',
        {
          'bg-grey-50': props.disabled,
          'border-warning-700': props.invalid,
          'border border-grey-300 border-solid': !props.multiple,
          'border-0 bg-transparent': props.multiple,
          'bg-white': !props.disabled && !props.multiple,
          'border-grey-300/70': props.disabled && !props.multiple,
        },
        // States
        { 'focus:outline-none': !props.multiple },
        // Transition
        'transition-all duration-200',
        // Font size
        {
          'text-xs': props.size === 'small',
          'text-xl': props.size === 'large',
        },
        {
          'outline-none outline-offset-0 ring-1 ring-primary-500': state.focused,
        },
      ],
    },
  }),
  dropdown: ({ props }: Record<string, any>) => ({
    class: [
      {
        hidden: props.loading,
      },
      'absolute top-0 right-0 bottom-0',
      'text-sm leading-none',
      'items-center inline-flex text-center align-bottom',
      'px-4 py-1.5',
      'text-inherit',
      'outline-none outline-0',
    ],
  }),
  loader: ({ props }: Record<string, any>) => ({
    class: [
      // disable the built-in loader when querying. Due to a 'feature', it's very difficult to reset the loading state when cancelling a query.
      // https://github.com/primefaces/primevue/issues/5601#issuecomment-2076067073
      // Instead, it's better to control the loading state manually in the search function, in which case props.loading will be true and the loader will be shown.
      { hidden: !props.loading },
      'text-sm leading-none bg-transparent',
      'absolute top-[50%] [translate:0_-50%] animate-spin',
      'right-4',
    ],
    'data-testid': 'v-select-loader',
  }),
  overlay: {
    class: [
      // Shape
      'box-content',
      'border border-solid border-grey-200',
      'rounded-lg',
      'shadow',
      'overflow-auto',
      // Color
      'bg-white',
      'text-[16px] font-sans font-normal text-black',
    ],
  },
  list: {
    class: 'py-2 list-none m-0',
  },
  option: ({ context }: Record<string, any>) => ({
    class: [
      'empty:em:h-4 empty:box-content',
      // Font
      'leading-none',
      { 'font-normal': !context.selected, 'font-bold': context.selected },
      // Position
      'relative',
      // Shape
      'border-0',
      'rounded-none',
      // Spacing
      'm-0',
      'py-2 px-4',
      // Color
      {
        'bg-primary-300': context.focused && !context.selected,
      },
      {
        'bg-primary-300': context.focused && context.selected,
      },
      { 'bg-transparent': !context.focused && context.selected },
      // States
      'hover:bg-primary-300',
      // Misc
      'cursor-pointer',
      'group/option',
    ],
  }),
  optionGroup: {
    class: ['font-bold', 'm-0', 'py-2 px-4', 'bg-white', 'cursor-auto'],
  },
  emptyMessage: {
    class: ['leading-none', 'py-2 px-4', 'bg-transparent'],
  },
  transition: {
    enterFromClass: 'opacity-0 scale-y-[0.8]',
    enterActiveClass:
      'transition-[transform,opacity] duration-[120ms] ease-[cubic-bezier(0,0,0.2,1)]',
    leaveActiveClass: 'transition-opacity duration-100 ease-linear',
    leaveToClass: 'opacity-0',
  },

  // styling for Autocomplete in multiple select mode
  inputMultiple: ({ props }: Record<string, any>) => ({
    class: [
      // Font
      'font-sans leading-none',
      {
        'text-xs': props.size === 'small',
        'text-xl': props.size === 'large',
      },
      // Flex
      'flex items-center flex-wrap',
      'gap-1',
      // Spacing
      'm-0 list-none',
      'px-4 py-1',
      {
        '!pr-[2.875rem]': props.dropdown,
      },
      // Size
      'w-full',
      // Shape
      'appearance-none rounded-lg',
      // Color
      {
        'text-grey-800': !props.disabled,
      },
      {
        'bg-white': !props.disabled,
        'bg-grey-50': props.disabled,
      },
      // Transition
      'transition duration-200 ease-in-out',
      // Misc
      'cursor-text overflow-hidden',
      'outline-none',
      'border border-solid border-grey-300',
      'w-80 max-w-full',
    ],
  }),
  chipItem: {
    class: ['max-w-full'],
  },
  input: ({ props }: Record<string, any>) => ({
    class: [
      // Font
      'font-sans leading-none',
      {
        'text-xs': props.size === 'small',
        'text-xl': props.size === 'large',
      },
      // Position
      'z-0',
      // Shape
      'appearance-none outline-none rounded-lg',
      { 'shadow-none rounded-none': props.multiple },
      // Size
      { 'min-h-11': !props.multiple },
      {
        'min-h-8': props.size === 'small',
      },
      { 'w-full': props.multiple },
      // Spacing
      'm-0',
      {
        'em:py-2 em:px-3': !props.multiple,
        'p-0': props.multiple,
      },
      // Colors
      'focus:filter-none hover:filter-none text-inherit',
      {
        'bg-grey-50': props.disabled,
        'border-warning-700': props.invalid,
        'border border-grey-300 border-solid': !props.multiple,
        'border-0 bg-transparent': props.multiple,
        'bg-white': !props.disabled && !props.multiple,
        'border-grey-300/70': props.disabled && !props.multiple,
      },
      // States
      { 'focus:outline-none': !props.multiple },
      // Transition
      'transition-all duration-200',
    ],
  }),
  inputChip: {
    class: ['py-1.5 px-0', 'inline-flex flex-auto'],
  },
  pcChip: ({ parent }: Record<string, any>) => ({
    root: {
      class: [
        '!inline-flex items-center',
        '!py-1.5 px-3',
        '!text-sm',
        '!rounded',
        '!border',
        '!text-primary-900',
        '!border-grey-200',
        '!max-w-full',
        '!normal-case',
        '!font-normal',
        {
          '!bg-white': !parent.props.disabled,
          '!bg-grey-50': parent.props.disabled,
        },
      ],
    },
    label: {
      class: ['max-w-full', 'overflow-x-hidden', 'text-ellipsis', 'leading-5', 'whitespace-nowrap'],
    },
  }),
});
